/* eslint-disable react/jsx-props-no-spreading */
import { useMsal } from '@azure/msal-react';
import { Box, Link } from '@mui/material';
import { CompanyRole } from 'constants/CompanyRole';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import { loginAction, ssoLoginAction } from 'store/actions/auth-actions';
import { setMenuStatusAction } from 'store/actions/menu-actions';
import {
  authErrorSelector,
  authErrorTextSelector,
  authLoadingSelector,
  authMfaErrorKeySelector,
} from 'store/selectors/auth-selectors';
import ILoginFormInputs from 'types/auth/ILoginFormInputs';
import IToken from 'types/auth/IToken';
import { AgencySettingKey } from 'services/agency-settings/types';
import { agencySettingsStore } from 'services/agency-settings';
import {
  LOGIN__CONTACT_US,
  LOGIN__CREATE_ACCOUNT,
  LOGIN__EMAIL_LABEL,
  LOGIN__EMAIL_MAX_LENGTH_ERROR,
  LOGIN__EMAIL_REQUIRED_ERROR,
  LOGIN__FORGOT_PASSWORD,
  LOGIN__INVALID_CREDENTIALS,
  LOGIN__PAGE_QUOTE,
  LOGIN__PAGE_TITLE,
  LOGIN__PASSWORD_LABEL,
  LOGIN__PASSWORD_REQUIRED_ERROR,
  LOGIN__SIGN_IN,
} from '../../translations/constants';
import AuthFooter from './AuthFooter';
import AuthHeader from './AuthHeader';
import {
  AuthContainer,
  AuthCreateAccountTitle,
  AuthLeftContainer,
  AuthRightContainer,
  AuthSignIn,
  AuthSignInContainer,
  AuthTitle,
  AuthUPButton,
  AuthUPInput,
  ErrorText,
} from './styles';

const Auth = (): JSX.Element => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ILoginFormInputs>({ defaultValues: { Email: '', Password: '' } });
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [passwordPolicyError, setPasswordPolicyError] = useState<string>('');
  const [agencySettings, setAgencySettings] = useState(agencySettingsStore.get());

  const location = useLocation();
  const [mfaErrorKey] = useState<string>((location.state as { mfaErrorKey?: string })?.mfaErrorKey ?? '');

  // MFA enrollment sends the user back here for the three failures it cannot recover from, and the
  // acceptance criteria require them to see why. location.state survives a reload via history.state,
  // so clear it once read - otherwise "your session has expired" haunts the login screen on every
  // refresh. The message itself is already captured in state above, so dropping it here is safe.
  //
  // Only the `usr` slot is cleared, not the whole history state: react-router v6 keeps its own `key`
  // and `idx` alongside it, and wiping those breaks its navigation bookkeeping.
  useEffect(() => {
    if ((location.state as { mfaErrorKey?: string })?.mfaErrorKey) {
      window.history.replaceState({ ...window.history.state, usr: null }, document.title);
    }
  }, [location.state]);

  const authLoading = useAppSelector(authLoadingSelector);
  const authError = useAppSelector(authErrorSelector);
  const authErrorText = useAppSelector(authErrorTextSelector);
  // Set when /Auth/login itself answered with an [MFA] key rather than routing the user onward.
  const loginMfaErrorKey = useAppSelector(authMfaErrorKeySelector);

  const getEmailErrorText = useCallback(() => {
    if (errors.Email?.type === 'required') {
      return t(LOGIN__EMAIL_REQUIRED_ERROR);
    }
    if (errors.Email?.type === 'maxLength') {
      return t(LOGIN__EMAIL_MAX_LENGTH_ERROR);
    }
    return '';
  }, [t, errors]);

  const getPasswordErrorText = useCallback(() => {
    if (errors.Password?.type === 'required') {
      return t(LOGIN__PASSWORD_REQUIRED_ERROR);
    }

    if (authError) {
      return t(LOGIN__INVALID_CREDENTIALS);
    }

    if (passwordPolicyError) {
      return passwordPolicyError;
    }
    return '';
  }, [t, errors, authError, passwordPolicyError]);

  const { instance } = useMsal();

  const handleLoginPopup = useCallback(
    async (pass: string) => {
      /**
       * When using popup and silent APIs, we recommend setting the redirectUri to a blank page or a page
       * that does not implement MSAL. Keep in mind that all redirect routes must be registered with the application
       * For more information, please follow this link: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/login-user.md#redirecturi-considerations
      
       * Scopes you add here will be prompted for user consent during sign-in.
       * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
       * For more information about OIDC scopes, visit:
       * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
       */

      await instance
        .loginPopup({
          scopes: ['User.Read'],
          // scopes: [`api://${getConfig('AZURE_AD_API_CLIENT_ID')}/api_user_access`],
          prompt: 'select_account', // to force login every time
          redirectUri: `${window.location.origin}/redirect`,
        })
        .then(response => {
          const { account } = response;
          dispatch(ssoLoginAction({ email: account.username, password: pass }));
          dispatch(setMenuStatusAction('Dashboard'));
        })
        .catch(error => {
          console.error('Login failed:', error);
        });
    },
    [instance, dispatch],
  );

  const onLogin = useCallback(
    async (data: ILoginFormInputs): Promise<void> => {
      try {
        const res = await dispatch(loginAction({ email: data.Email, password: data.Password }));
        const response = res.payload as IToken;
        if (!response) {
          return;
        }
        if (response.needsReset) {
          navigate(`/auth/set-password/${response.token}`);
          return;
        }
        // Enrollment wins over the MSAL/hasMultifactor popup — see the design doc, section 6.
        if (response.requiresMfaEnrollment) {
          navigate('/auth/mfa-enrollment', { state: { mfaToken: response.mfaToken } });
          return;
        }
        // Same precedence, and for the same reason: `hasMultifactor` is the old MSAL SSO switch and is
        // unrelated to TOTP. It also could not run after a challenge — the popup needs the password,
        // which this screen no longer holds by then.
        if (response.requiresMfaChallenge) {
          navigate('/auth/mfa-challenge', { state: { mfaToken: response.mfaToken } });
          return;
        }
        if (response.role === CompanyRole.ADMIN && response.hasMultifactor) {
          await handleLoginPopup(data.Password);
        } else {
          dispatch(setMenuStatusAction('Dashboard'));
        }
      } catch (error) {
        console.error('Login error:', error);
      }
    },
    [dispatch, navigate, handleLoginPopup],
  );

  const onKeyPress = useCallback(
    (event: any): void => {
      const code = event.keyCode || event.which;
      if (code === 13) {
        handleSubmit(onLogin)();
      }
    },
    [handleSubmit, onLogin],
  );

  useEffect(() => {
    const loadSettings = async () => {
      const settings = await agencySettingsStore.getOrLoad();
      setAgencySettings(settings);
    };

    if (!agencySettings) {
      loadSettings();
    }
  }, [agencySettings]);

  return (
    <Box>
      <AuthHeader />
      <AuthContainer>
        <AuthLeftContainer>
          <AuthTitle>{t(LOGIN__PAGE_QUOTE)}</AuthTitle>
        </AuthLeftContainer>
        <AuthRightContainer>
          <AuthSignInContainer>
            <AuthSignIn>{t(LOGIN__PAGE_TITLE)}</AuthSignIn>
            <Controller
              name="Email"
              control={control}
              rules={{ required: true, maxLength: 100 }}
              render={({ field }) => (
                <AuthUPInput
                  value={field.value}
                  label={t(LOGIN__EMAIL_LABEL)}
                  placeholder={t(LOGIN__EMAIL_LABEL)}
                  error={!!errors.Email || authError}
                  required
                  type="email"
                  helperText={getEmailErrorText()}
                  onChange={field.onChange}
                  onKeyPress={event => onKeyPress(event)}
                />
              )}
            />
            <Controller
              name="Password"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <AuthUPInput
                  value={field.value}
                  label={t(LOGIN__PASSWORD_LABEL)}
                  placeholder={t(LOGIN__PASSWORD_LABEL)}
                  type="password"
                  required
                  error={!!errors.Password || authError}
                  helperText={getPasswordErrorText()}
                  onChange={field.onChange}
                  onKeyPress={event => onKeyPress(event)}
                />
              )}
            />
            {(mfaErrorKey || loginMfaErrorKey) && <ErrorText>{t(mfaErrorKey || loginMfaErrorKey)}</ErrorText>}
            <Box display="flex" gap="10px">
              <AuthUPButton
                text={t(LOGIN__SIGN_IN)}
                onClick={() => {
                  handleSubmit(onLogin)();
                }}
                disabled={authLoading}
              />
            </Box>
            {authErrorText && <p>Unable to login with Microsoft Account!</p>}
            <Link href="/forgotpassword">{t(LOGIN__FORGOT_PASSWORD)}</Link>
            <AuthCreateAccountTitle>{t(LOGIN__CREATE_ACCOUNT)}</AuthCreateAccountTitle>
            <Link href={agencySettings?.[AgencySettingKey.ContactUsUrl] ?? '#'} target="_blank">
              <AuthUPButton text={t(LOGIN__CONTACT_US)} onClick={null} outlined />
            </Link>
          </AuthSignInContainer>
        </AuthRightContainer>
      </AuthContainer>
      <AuthFooter />
    </Box>
  );
};

export default Auth;
